import { apiFetch, readJson, readJsonArray, readErrorMessage } from './client';
import type { ListSyncRunsOptions, SyncRun, SyncRunListPage } from './syncJobs';

export type TimeOffType = 'Vacation' | 'Sick' | 'Personal' | 'Other';
export type TimeOffStatus = 'Pending' | 'Approved' | 'Rejected' | 'Cancelled';
export type CalendarOwnerKind = 'UserMailbox' | 'GroupMailbox';
export type CalendarSyncDirection = 'PushOnly' | 'Bidirectional';

export interface TimeOffEntryCalendarLink {
  calendarTargetId: string;
  calendarTargetDisplayName: string;
  graphEventId: string | null;
  lastSyncedAt: string | null;
  lastSyncResult: string | null;
  isDirty: boolean;
}

export interface TimeOffEntry {
  id: string;
  employeeUpn: string;
  employeeDisplayName: string | null;
  needsEmployeeIdentity: boolean;
  type: TimeOffType;
  startUtc: string;
  endUtc: string;
  isAllDay: boolean;
  notes: string | null;
  status: TimeOffStatus;
  createdAt: string;
  createdBy: string | null;
  updatedAt: string | null;
  updatedBy: string | null;
  rowVersion: number;
  calendarLinks: TimeOffEntryCalendarLink[];
}

export interface CreateTimeOffEntryPayload {
  employeeUpn: string;
  employeeDisplayName?: string | null;
  type: TimeOffType;
  startUtc: string;
  endUtc: string;
  isAllDay: boolean;
  notes?: string | null;
  status?: TimeOffStatus;
  calendarTargetIds: string[];
}

export interface UpdateTimeOffEntryPayload {
  employeeUpn?: string;
  employeeDisplayName?: string | null;
  type?: TimeOffType;
  startUtc?: string;
  endUtc?: string;
  isAllDay?: boolean;
  notes?: string | null;
  status?: TimeOffStatus;
  calendarTargetIds?: string[];
  rowVersion: number;
}

export type CalendarTargetSegment = 'Company' | 'Contract';

export interface CalendarTarget {
  id: string;
  displayName: string;
  segment: CalendarTargetSegment;
  graphCalendarOwnerKind: CalendarOwnerKind;
  graphOwnerId: string;
  graphCalendarId: string | null;
  isEnabled: boolean;
  syncDirection: CalendarSyncDirection;
  hasInboundCalendarEventsDeltaAnchor: boolean;
}

export interface DirectoryUser {
  id: string;
  displayName: string;
  mail: string | null;
  userPrincipalName: string | null;
}

export interface ImportRowError {
  rowNumber: number;
  field: string;
  message: string;
  employeeUpn?: string | null;
}

export interface ImportResult {
  inserted: number;
  skipped: number;
  errors: ImportRowError[];
}

export interface SyncStatus {
  lastStartedAt: string | null;
  lastFinishedAt: string | null;
  lastSummary: string | null;
  lastTriggerKind: string | null;
  lastTriggeredBy: string | null;
  lastErrors: number;
  lastProcessed: number;
  nextFireTimeUtc: string | null;
  dirtyLinks: number;
}

export interface TimeOffDashboardUpcoming {
  id: string;
  employeeUpn: string;
  employeeDisplayName: string | null;
  type: TimeOffType;
  startUtc: string;
  endUtc: string;
  status: TimeOffStatus;
}

export interface TimeOffDashboardTypeCount {
  type: TimeOffType;
  count: number;
}

export interface TimeOffDashboardAttentionItem {
  id: string;
  employeeUpn: string;
  employeeDisplayName: string | null;
  type: TimeOffType;
  startUtc: string;
  endUtc: string;
  status: TimeOffStatus;
  reasons: string[];
}

export interface TimeOffDashboardSummary {
  todayOutCount: number;
  nextSevenDaysCount: number;
  pendingCount: number;
  needsIdentityCount: number;
  dirtyLinks: number;
  lastSyncStatus: SyncStatus;
  upcoming: TimeOffDashboardUpcoming[];
  byType: TimeOffDashboardTypeCount[];
  attentionItems: TimeOffDashboardAttentionItem[];
}

export interface ListTimeOffOptions {
  from?: string;
  to?: string;
  status?: TimeOffStatus;
  employeeUpn?: string;
  company?: string;
  department?: string;
  skip?: number;
  take?: number;
}

export interface TimeOffOrgOptions {
  companies: string[];
  departments: string[];
}

function toQueryString(params: Record<string, string | number | undefined>): string {
  const parts = Object.entries(params)
    .filter(([, v]) => v !== undefined && v !== '')
    .map(([k, v]) => `${encodeURIComponent(k)}=${encodeURIComponent(String(v))}`);
  return parts.length === 0 ? '' : `?${parts.join('&')}`;
}

export async function listTimeOff(opts: ListTimeOffOptions = {}): Promise<TimeOffEntry[]> {
  const qs = toQueryString({ ...opts });
  const res = await apiFetch(`/api/time-off${qs}`);
  if (!res.ok) throw new Error(`Failed to list time off: ${res.status}`);
  return readJson<TimeOffEntry[]>(res);
}

export async function listTimeOffOrgOptions(): Promise<TimeOffOrgOptions> {
  const res = await apiFetch('/api/time-off/directory/org-options');
  if (!res.ok) throw new Error(await readErrorMessage(res));
  return readJson<TimeOffOrgOptions>(res);
}

async function readTimeOffMutateError(res: Response): Promise<string> {
  const text = await res.text();
  if (!text.trim()) return `Failed to save time off entry: ${res.status}`;
  try {
    const data = JSON.parse(text) as { error?: unknown; message?: unknown };
    if (typeof data.message === 'string' && data.message.trim()) return data.message;
    if (typeof data.error === 'string' && data.error.trim()) return data.error;
  } catch {
    /* use raw body */
  }
  return text.slice(0, 800);
}

export async function createTimeOff(payload: CreateTimeOffEntryPayload): Promise<TimeOffEntry> {
  const res = await apiFetch('/api/time-off', {
    method: 'POST',
    body: JSON.stringify(payload)
  });
  if (!res.ok) throw new Error(await readTimeOffMutateError(res));
  return readJson<TimeOffEntry>(res);
}

export interface BulkCreateTimeOffRowPayload {
  type: TimeOffType;
  startUtc: string;
  endUtc: string;
  isAllDay: boolean;
  notes?: string | null;
  status?: TimeOffStatus;
}

export interface BulkCreateTimeOffPayload {
  employeeUpn: string;
  employeeDisplayName?: string | null;
  calendarTargetIds: string[];
  rows: BulkCreateTimeOffRowPayload[];
}

export interface BulkCreateTimeOffResult {
  inserted: number;
  errors: ImportRowError[];
  created: TimeOffEntry[];
}

function isBulkCreateTimeOffResult(value: unknown): value is BulkCreateTimeOffResult {
  if (!value || typeof value !== 'object') return false;
  const v = value as Record<string, unknown>;
  return (
    typeof v.inserted === 'number' &&
    Array.isArray(v.errors) &&
    Array.isArray(v.created)
  );
}

export async function bulkCreateTimeOff(payload: BulkCreateTimeOffPayload): Promise<BulkCreateTimeOffResult> {
  const res = await apiFetch('/api/time-off/bulk', {
    method: 'POST',
    body: JSON.stringify(payload)
  });
  if (res.ok) {
    return readJson<BulkCreateTimeOffResult>(res);
  }
  if (res.status === 400) {
    // Server returns BulkCreateTimeOffResultDto with row-level errors on validation failure.
    const body = await readJson<unknown>(res);
    if (isBulkCreateTimeOffResult(body)) {
      return body;
    }
  }
  throw new Error(await readErrorMessage(res));
}

export async function updateTimeOff(id: string, payload: UpdateTimeOffEntryPayload): Promise<TimeOffEntry> {
  const res = await apiFetch(`/api/time-off/${encodeURIComponent(id)}`, {
    method: 'PATCH',
    body: JSON.stringify(payload)
  });
  if (!res.ok) throw new Error(await readErrorMessage(res));
  return readJson<TimeOffEntry>(res);
}

export async function cancelTimeOff(id: string): Promise<void> {
  const res = await apiFetch(`/api/time-off/${encodeURIComponent(id)}`, { method: 'DELETE' });
  if (!res.ok && res.status !== 204) throw new Error(await readErrorMessage(res));
}

export async function purgeTimeOff(id: string): Promise<void> {
  const res = await apiFetch(`/api/time-off/${encodeURIComponent(id)}/purge`, { method: 'POST' });
  if (!res.ok && res.status !== 204) throw new Error(await readErrorMessage(res));
}

export async function listCalendarTargets(): Promise<CalendarTarget[]> {
  const res = await apiFetch('/api/time-off/calendars');
  if (!res.ok) throw new Error(await readErrorMessage(res));
  return readJsonArray<CalendarTarget>(res);
}

export async function searchEnabledDirectoryUsers(q: string): Promise<DirectoryUser[]> {
  const res = await apiFetch(`/api/time-off/directory/users${toQueryString({ q })}`);
  if (!res.ok) throw new Error(await readErrorMessage(res));
  return readJsonArray<DirectoryUser>(res);
}

export async function importTimeOffCsv(file: File, calendarTargetIds: string[]): Promise<ImportResult> {
  const form = new FormData();
  form.append('file', file);
  form.append('calendarTargetIds', calendarTargetIds.join(','));
  const res = await apiFetch('/api/time-off/import/csv', {
    method: 'POST',
    body: form
  });
  if (!res.ok) throw new Error(`Failed to import CSV: ${res.status}`);
  return readJson<ImportResult>(res);
}

export function downloadCsvTemplateUrl(): string {
  return '/api/time-off/import/template';
}

export async function triggerSync(): Promise<void> {
  const res = await apiFetch('/api/time-off/sync', { method: 'POST' });
  if (!res.ok && res.status !== 202) throw new Error(`Failed to trigger sync: ${res.status}`);
}

export async function fetchSyncStatus(): Promise<SyncStatus> {
  const res = await apiFetch('/api/time-off/sync/status');
  if (!res.ok) throw new Error(`Failed to load sync status: ${res.status}`);
  return readJson<SyncStatus>(res);
}

export async function fetchTimeOffDashboardSummary(): Promise<TimeOffDashboardSummary> {
  const res = await apiFetch('/api/time-off/dashboard/summary');
  if (!res.ok) throw new Error(await readErrorMessage(res));
  return readJson<TimeOffDashboardSummary>(res);
}

export async function listTimeOffSyncRuns(opts: Omit<ListSyncRunsOptions, 'jobName'> = {}): Promise<SyncRunListPage> {
  const params = Object.entries(opts)
    .filter(([, v]) => v !== undefined && v !== '')
    .map(([k, v]) => `${encodeURIComponent(k)}=${encodeURIComponent(String(v))}`);
  const qs = params.length === 0 ? '' : `?${params.join('&')}`;
  const res = await apiFetch(`/api/time-off/sync-runs${qs}`);
  if (!res.ok) throw new Error(`Failed to list time off sync runs: ${res.status}`);
  return readJson<SyncRunListPage>(res);
}

export async function getTimeOffSyncRun(id: string): Promise<SyncRun> {
  const res = await apiFetch(`/api/time-off/sync-runs/${encodeURIComponent(id)}`);
  if (!res.ok) throw new Error(`Failed to load time off sync run: ${res.status}`);
  return readJson<SyncRun>(res);
}
